// Core packages
import { NgModule } from "@angular/core";
import { RouterModule, Routes } from "@angular/router";

// Third party packages

// Custom packages - Misc
import { AllPermissionsGuard } from "src/app/shared/guards/all-permissions.guard";
import { ArchiveV3RedirectGuard } from "src/app/shared/guards/archive-v3-redirect.guard";
import { RoleGuard } from "src/app/shared/guards/role.guard";
import {
  ARCHIVE_V3_PROPERTY_DEADLINES,
  ARCHIVE_V3_PROPERTY_DOCUMENTS,
  ARCHIVE_V3_PROPERTY_INSERT,
  ARCHIVE_V3_PROPERTY_LATEST_DOCUMENTS,
  ARCHIVE_V3_PROPERTY_SEARCH,
} from "src/app/shared/utils/archive-v3-redirect.utils";

// Custom packages - Component
import { AddPropertyComponent } from "src/app/modules/properties/pages/add-property/add-property.page";
import { PropertiesListComponent } from "src/app/modules/properties/pages/properties-list/properties-list.page";
import { PropertyAddDocumentsComponent } from "src/app/modules/properties/pages/property-add-documents/property-add-documents.page";
import { PropertyAddNewDocumentsComponent } from "src/app/modules/properties/pages/property-add-new-documents/property-add-new-documents.page";
import { PropertyBalancesComponent } from "src/app/modules/properties/pages/property-balances/property-balances.page";
import { PropertyCaretakersComponent } from "src/app/modules/properties/pages/property-caretakers/property-caretakers.page";
import { PropertyCondominiumAccessComponent } from "src/app/modules/properties/pages/property-condominium-access/property-condominium-access.page";
import { PropertyDetail2Component } from "src/app/modules/properties/pages/property-detail2/property-detail2.page";
import { PropertyDocumentsWithExpiration } from "src/app/modules/properties/pages/property-documents-expiration/property-documents-expiration.page";
import { PropertyDocumentsComponent } from "src/app/modules/properties/pages/property-documents/property-documents.page";
import { PropertyHomeComponent } from "src/app/modules/properties/pages/property-home/property-home.page";
import { PropertyLatestDocumentsInsertedComponent } from "src/app/modules/properties/pages/property-latest-documents-inserted/property-latest-documents-inserted.page";
import { PropertyMappingComponent } from "src/app/modules/properties/pages/property-mapping/property-mapping.page";
import { PropertyNotesContractualAgreements } from "src/app/modules/properties/pages/property-notes-contractual/property-notes-contractual.page";
import { PropertyPropertyPropertyTagNfcList } from "src/app/modules/properties/pages/property-property-tag-nfc-list/property-property-tag-nfc-list.page";
import { PropertyPropertyPropertyTangNfc } from "src/app/modules/properties/pages/property-property-tang-nfc/property-property-tang-nfc.page";
import { PropertyStatusComponent } from "src/app/modules/properties/pages/property-status/property-status.page";
import { PropertyTagNfcListComponent } from "src/app/modules/properties/pages/property-tag-nfc-list/property-tag-nfc-list.page";
import { ViewPropertyComponent } from "src/app/modules/properties/pages/view-property/view-property.page";
import { PropertiesNfcListComponent } from "./pages/properties-nfc-list/properties-nfc-list.page";
import { PropertiesSuspendedListComponent } from './pages/properties-suspended-list/properties-suspended-list.page';
import { SearchDocumentsComponent } from './pages/property-documents/search-documents/search-documents.page.component';

// The legacy pages of the archive that name an `archiveV3Redirect` open their 3.0.0 successor once
// the archive is live (FE-A1: ArchiveV3RedirectGuard, first in their canActivate); until the switch
// they open exactly as before.
const routes: Routes = [
  {
    path: 'my-property',
    component: ViewPropertyComponent,
    canActivate: [RoleGuard],
    data: {
      permissions: ['canReadPropertiesList']
    },
  },
  {
    path: 'my-property/:propertyId',
    component: ViewPropertyComponent,
    canActivate: [ArchiveV3RedirectGuard, RoleGuard],
    data: {
      permissions: ['canReadPropertiesList'],
      archiveV3Redirect: ARCHIVE_V3_PROPERTY_DOCUMENTS,
    },
  },
  {
    path: "list",
    component: PropertiesListComponent,
    canActivate: [RoleGuard],
    data: {
      properties: ["canReadPropertiesList"]
    }
  },
  {
    path: "tag-nfc-list",
    component: PropertiesNfcListComponent,
    canActivate: [RoleGuard],
    data: {
      properties: ["canReadPropertiesList"]
    }
  },
  {
    path: "suspended",
    component: PropertiesSuspendedListComponent,
    canActivate: [RoleGuard],
    data: {
      properties: ["canReadPropertiesList"]
    }
  },
  {
    path: ":propertyId/home",
    component: PropertyHomeComponent,
    canActivate: [RoleGuard],
    data: {
      properties: ["canReadPropertiesList"],
    }
  },
  // {
  //   path: ":propertyId/data",
  //   component: PropertyDetailComponent,
  //   canActivate: [RoleGuard],
  //   data: {
  //     properties: ["canReadPropertiesList"] //"canCreateProperties",
  //   }
  // },
  {
    path: ":propertyId/tagNfgList",
    component: PropertyTagNfcListComponent,
    canActivate: [RoleGuard],
    data: {
      properties: ["canReadPropertiesList"]
    }
  },
  {
    path: ":propertyId/mapping/:mappingFormActiveSection/:itemId",
    component: PropertyMappingComponent,
    canActivate: [RoleGuard],
    data: {
      properties: ["canCreateProperties"]
    }
  },
  {
    path: ":propertyId/mapping/:mappingFormActiveSection",
    component: PropertyMappingComponent,
    canActivate: [RoleGuard],
    data: {
      properties: ["canCreateProperties"]
    }
  },
  {
    path: ":propertyId/mapping",
    component: PropertyMappingComponent,
    canActivate: [RoleGuard],
    data: {
      properties: ["canCreateProperties"]
    }
  },
  {
    path: ":propertyId/documents/:activeSection",
    component: PropertyDocumentsComponent,
    canActivate: [ArchiveV3RedirectGuard, RoleGuard],
    data: {
      properties: ["canCreateProperties"],
      archiveV3Redirect: ARCHIVE_V3_PROPERTY_DOCUMENTS,
    }
  },
  {
    path: ":propertyId/documents",
    component: PropertyDocumentsComponent,
    canActivate: [ArchiveV3RedirectGuard, RoleGuard],
    data: {
      properties: ["canCreateProperties"],
      archiveV3Redirect: ARCHIVE_V3_PROPERTY_DOCUMENTS,
    }
  },
  // Document dossier rebuilt on the 3.0.0 collections. Reachable from the bar of the new archive
  // pages only, on purpose: nothing on the legacy pages points here while it is validated against
  // the legacy page.
  // The three pages of the new archive below are loaded on their own, out of the initial bundle:
  // this module is also imported by AppModule. Guard and permissions stay here.
  {
    path: ":propertyId/documentsV3",
    loadChildren: () =>
      import("./pages/property-documents-v3/property-documents-v3.module").then(
        (m) => m.PropertyDocumentsV3Module
      ),
    canActivate: [RoleGuard],
    data: {
      permissions: ["canReadDocumentsList"]
    }
  },
  // Search within the documents of one property on the 3.0.0 collections, the successor of
  // ":propertyId/search". BOTH permissions, hence AllPermissionsGuard: it reads the same archive tree
  // as the dossier above (canReadDocumentsList), and it follows the role switch "Ricerca documenti"
  // (canSearchDocuments) as the side menu does — the bar of the new archive pages offers it on the
  // same two. Reachable from that bar, not from the legacy pages, until the cut-over.
  {
    path: ":propertyId/searchV3",
    loadChildren: () =>
      import("./pages/property-search-documents-v3/property-search-documents-v3.module").then(
        (m) => m.PropertySearchDocumentsV3Module
      ),
    canActivate: [AllPermissionsGuard],
    data: {
      permissions: ["canReadDocumentsList", "canSearchDocuments"]
    }
  },
  // Document insert on the 3.0.0 collections, the successor of addDocuments and addNewDocuments.
  // Reachable from the bar of the new archive pages only, like the dossier above, until the
  // cut-over links it. BOTH permissions, hence AllPermissionsGuard: the page reads the archive tree
  // (canReadDocumentsList) and writes (canCreateDocuments), and with only one of them every save
  // would answer 403.
  {
    path: ":propertyId/addNewDocumentsV3",
    loadChildren: () =>
      import("./pages/property-add-new-documents-v3/property-add-new-documents-v3.module").then(
        (m) => m.PropertyAddNewDocumentsV3Module
      ),
    canActivate: [AllPermissionsGuard],
    data: {
      permissions: ["canReadDocumentsList", "canCreateDocuments"]
    }
  },
  {
    path: "add",
    component: AddPropertyComponent,
    canActivate: [RoleGuard],
    data: {
      properties: ["canCreateProperties"]
    }
  },
  {
    path: ":propertyId/caretakers",
    component: PropertyCaretakersComponent,
    canActivate: [RoleGuard],
    data: {
      properties: ["canCreateProperties"]
    }
  },
  {
    path: ":propertyId/addDocuments",
    component: PropertyAddDocumentsComponent,
    canActivate: [ArchiveV3RedirectGuard, RoleGuard],
    data: {
      properties: ["canCreateDocuments"],
      archiveV3Redirect: ARCHIVE_V3_PROPERTY_INSERT,
    }
  },
  {
    path: ":propertyId/addNewDocuments",
    component: PropertyAddNewDocumentsComponent,
    canActivate: [ArchiveV3RedirectGuard, RoleGuard],
    data: {
      properties: ["canCreateDocuments"],
      archiveV3Redirect: ARCHIVE_V3_PROPERTY_INSERT,
    }
  },
  {
    path: ":propertyId/search",
    component: SearchDocumentsComponent,
    canActivate: [ArchiveV3RedirectGuard, RoleGuard],
    data: {
      properties: ["canCreateDocuments"],
      archiveV3Redirect: ARCHIVE_V3_PROPERTY_SEARCH,
    }
  },
  {
    path: ":propertyId/documentsWithExpiration",
    component: PropertyDocumentsWithExpiration,
    canActivate: [ArchiveV3RedirectGuard, RoleGuard],
    data: {
      properties: ["canReadPropertiesList"],
      archiveV3Redirect: ARCHIVE_V3_PROPERTY_DEADLINES,
    }
  },
  // «Elenco documenti con scadenza» on the 3.0.0 collections (NEW-M15), the successor of the route
  // above, which stays as it is. Loaded on its own, out of the initial bundle, as the other pages of
  // the new archive; reachable by URL only until the switch. The permission of the back-end route, and
  // the four role types it serves (DEADLINES_V3_READER_ROLE_TYPES, written out: importing it here would
  // pull the rules of the page into the initial bundle): a guest or a tagger would only get a 403
  {
    path: ":propertyId/documentsWithExpirationV3",
    loadChildren: () =>
      import("./pages/property-documents-expiration-v3/property-documents-expiration-v3.module").then(
        (m) => m.PropertyDocumentsExpirationV3Module
      ),
    canActivate: [RoleGuard],
    data: {
      permissions: ["canReadDocumentsList"],
      roleTypes: ["admin", "operator", "master", "collaborator"],
    }
  },
  {
    path: ":propertyId/condominiumAccess",
    component: PropertyCondominiumAccessComponent,
    canActivate: [RoleGuard],
    data: {
      properties: ["canReadPropertiesList"],
    }
  },
  {
    path: ":propertyId/detail2",
    component: PropertyDetail2Component,
    canActivate: [RoleGuard],
    data: {
      properties: ["canReadPropertiesList"] //"canCreateProperties",
    }
  },
  {
    path: ":propertyId/latestDocumentsInserted",
    component: PropertyLatestDocumentsInsertedComponent,
    canActivate: [ArchiveV3RedirectGuard, RoleGuard],
    data: {
      properties: ["canReadPropertiesList"],
      archiveV3Redirect: ARCHIVE_V3_PROPERTY_LATEST_DOCUMENTS,
    }
  },
  // «Ultimi documenti inseriti» on the 3.0.0 collections (NEW-M16), the successor of the route above,
  // which stays as it is. Loaded on its own, out of the initial bundle, as the other pages of the new
  // archive; reachable by URL only until the switch. The permission of the back-end route, and the
  // roles the button of the home of the property is shown to: the guests have a block of their own
  // there, without it
  {
    path: ":propertyId/latestDocumentsInsertedV3",
    loadChildren: () =>
      import("./pages/property-latest-documents-inserted-v3/property-latest-documents-inserted-v3.module").then(
        (m) => m.PropertyLatestDocumentsInsertedV3Module
      ),
    canActivate: [RoleGuard],
    data: {
      permissions: ["canReadDocumentsList"],
      roleTypes: ["admin", "operator", "master", "collaborator"],
    }
  },
  {
    path: ":propertyId/status",
    component: PropertyStatusComponent,
    canActivate: [RoleGuard],
    data: {
      properties: ["canReadPropertiesList"],
    }
  },
  {
    path: ":propertyId/balances",
    component: PropertyBalancesComponent,
    canActivate: [RoleGuard],
    data: {
      properties: ["canReadPropertiesList"],
    }
  },
  {
    path: ":propertyId/notesContractualAgreements",
    component: PropertyNotesContractualAgreements,
    canActivate: [RoleGuard],
    data: {
      properties: ["canReadPropertiesList"],
    }
  },
  {
    path: ":propertyId/propertyPropertyTangNfc",
    component: PropertyPropertyPropertyTangNfc,
    canActivate: [RoleGuard],
    data: {
      properties: ["canReadPropertiesList"],
    }
  },
  {
    path: ":propertyId/propertyPropertyTagNfcList",
    component: PropertyPropertyPropertyTagNfcList,
    canActivate: [RoleGuard],
    data: {
      properties: ["canReadPropertiesList"],
    }
  },
  {
    path: "**",
    redirectTo: "it/errors/404"
  }
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule]
})
export class PropertiesRoutingModule {}
